import { CustomCalendarType } from "@ac-expo/api-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import {
  CustomCalendarTemplateForm,
  UpsertCustomCalendarForm,
} from "../schemas";
import {
  customCalendarQueryKeys,
  eventsQueryKeys,
  organiserCalendarTemplatesQueryKeys,
} from "../query-keys";

const upsertCustomCalendar = async (
  data: UpsertCustomCalendarForm
): Promise<CustomCalendarType> => {
  try {
    if (!data.id) {
      const { data: response } = await privateAxios.post<CustomCalendarType>(
        "/custom-calendar",
        data
      );
      return response;
    }
    const { data: response } = await privateAxios.patch<CustomCalendarType>(
      `/custom-calendar/${data.id}`,
      data
    );
    return response;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message;
    }
    throw error;
  }
};

export const useUpsertCustomCalendarMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpsertCustomCalendarForm) => upsertCustomCalendar(data),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(customCalendarQueryKeys.get(data.id), data);
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.getComplete(variables.eventId),
      });
    },
  });
};

const saveCustomCalendarTemplate = async (
  slot: number,
  body: CustomCalendarTemplateForm
) => {
  const { data } = await privateAxios.put<void>(
    `/custom-calendar/template/${slot}`,
    body
  );
  return data;
};

export const useSaveCustomCalendarTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slot,
      body,
    }: {
      slot: number;
      body: CustomCalendarTemplateForm;
    }) => saveCustomCalendarTemplate(slot, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organiserCalendarTemplatesQueryKeys.all,
      });
    },
  });
};

const deleteCustomCalendarTemplate = async (slot: number) => {
  const { data } = await privateAxios.delete<void>(
    `/custom-calendar/template/${slot}`
  );
  return data;
};

export const useDeleteCustomCalendarTemplateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ slot }: { slot: number }) =>
      deleteCustomCalendarTemplate(slot),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: organiserCalendarTemplatesQueryKeys.all,
      });
    },
  });
};
